import { Badge, Box, Button, Flex, Heading, Icon, Skeleton, Spinner, Text } from "@chakra-ui/react";
import type { ReactNode } from "react";
import type { IconType } from "react-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { KeyboardScrollBox } from "../../../common/KeyboardScrollBox";
import { useHomeLabel } from "../useHomeLabel";

/** The card itself. Split out so a card holding several tables can reuse the chrome. */
export const PanelCard = ({ children }: { children: ReactNode }) => (
  <Box bg="smBackground" rounded="2xl" border="1px solid" borderColor="smBorder" overflow="hidden">
    {children}
  </Box>
);

export const PanelHeader = ({
  titleKey,
  icon,
  count,
  seeAllTo,
  children,
}: {
  titleKey: string;
  icon?: IconType;
  count?: number;
  seeAllTo?: string;
  children?: ReactNode;
}) => {
  const { t } = useTranslation();
  const homeLabel = useHomeLabel();
  const navigate = useNavigate();
  return (
    <Flex align="center" justify="space-between" px="4" py="3" borderBottom="1px solid" borderColor="smBorder" gap="2">
      <Flex align="center" gap="2" minW="0">
        {icon && <Icon as={icon} color="smPrimary" boxSize="4" flexShrink={0} />}
        <Heading as="h2" size="sm" color="smText" noOfLines={1}>
          {homeLabel(titleKey)}
        </Heading>
        {!!count && (
          <Badge colorScheme="teal" rounded="full" px="2" fontSize="xs">
            {count}
          </Badge>
        )}
        {children}
      </Flex>
      {seeAllTo && (
        <Button variant="link" size="sm" colorScheme="teal" flexShrink={0} onClick={() => navigate(seeAllTo)}>
          {t("See all")} →
        </Button>
      )}
    </Flex>
  );
};

/**
 * Whichever of loading, failed, empty or content applies. Split out so each table inside a tabbed
 * card carries its own state rather than the card guessing on their behalf.
 */
export const PanelBody = ({
  isLoading,
  isError,
  isEmpty,
  emptyKey,
  children,
}: {
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  emptyKey: string;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const homeLabel = useHomeLabel();

  if (isLoading) {
    return (
      <Flex py="12" justify="center">
        <Spinner color="smPrimary" />
      </Flex>
    );
  }
  if (isError) {
    return (
      <Flex py="12" justify="center">
        <Text color="smError">{t("Could not load data.")}</Text>
      </Flex>
    );
  }
  if (isEmpty) {
    return (
      <Flex py="12" px="4" justify="center">
        <Text color="smMuted" fontSize="sm" textAlign="center">
          {homeLabel(emptyKey)}
        </Text>
      </Flex>
    );
  }
  // The card clips its corners, so the table needs its own scroll container — otherwise a table
  // wider than the panel is cut off with no way to reach the hidden columns.
  return <KeyboardScrollBox overflowX="auto">{children}</KeyboardScrollBox>;
};

interface Props {
  titleKey: string;
  icon?: IconType;
  count?: number;
  seeAllTo?: string;
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  emptyKey: string;
  children: ReactNode;
}

export const HomePanel = ({ titleKey, icon, count, seeAllTo, ...body }: Props) => (
  <PanelCard>
    <PanelHeader titleKey={titleKey} icon={icon} count={count} seeAllTo={seeAllTo} />
    <PanelBody {...body} />
  </PanelCard>
);

export const PanelFigure = ({
  value,
  label,
  qualifier,
  isLoading,
  isAlert = false,
  divider,
  onClick,
  testId,
}: {
  value: number | string;
  label: ReactNode;
  qualifier?: ReactNode;
  isLoading: boolean;
  isAlert?: boolean;
  divider: "top" | "bottom";
  onClick?: () => void;
  testId?: string;
}) => (
  <Flex
    {...(onClick && { as: "button", type: "button", onClick, _hover: { bg: "smBackgroundSecondary" } })}
    direction="column"
    align="flex-start"
    w="full"
    px="4"
    py="3"
    borderTop={divider === "top" ? "1px solid" : undefined}
    borderBottom={divider === "bottom" ? "1px solid" : undefined}
    borderColor="smBorder"
    textAlign="left"
    data-testid={testId}
  >
    {isLoading ? (
      <Skeleton h="30px" w="full" my="1" />
    ) : (
      <Text fontSize="2xl" color={isAlert ? "smError" : "smText"} lineHeight="1.2">
        {value}
      </Text>
    )}
    <Text fontSize="sm" color="smText">
      {label}
    </Text>
    {qualifier && (
      <Text fontSize="xs" color="smMuted" noOfLines={1}>
        {qualifier}
      </Text>
    )}
  </Flex>
);
